import type {
  ReportAnnotation,
  ReportGrain,
  ReportPoint
} from "@gettactic/api";

/**
 * A series read as a running total, for counts: each bucket carries
 * everything up to it. The previous period runs up on its own.
 */
export const cumulative = (series: ReportPoint[]): ReportPoint[] => {
  let value = 0;
  let previous = 0;
  return series.map((p) => {
    value += p.value;
    previous += p.previous ?? 0;
    return {
      ...p,
      value,
      previous: p.previous === null ? null : previous,
      parts: null
    };
  });
};

/** The moving-average window per grain: a week of days, a few weeks or months. */
export const smoothingWindow = (grain: ReportGrain): number =>
  grain === "day" ? 7 : 3;

/**
 * A series smoothed with a trailing moving average, so a noisy day grain
 * reads as a shape. The first buckets average what there is so far.
 */
export const smoothed = (
  series: ReportPoint[],
  window: number
): ReportPoint[] =>
  series.map((p, index) => {
    const from = Math.max(0, index - window + 1);
    const slice = series.slice(from, index + 1);
    const average = (
      pick: (q: ReportPoint) => number | null
    ): number | null => {
      const values = slice.map(pick).filter((v): v is number => v !== null);
      return values.length === 0
        ? null
        : values.reduce((sum, v) => sum + v, 0) / values.length;
    };
    return {
      ...p,
      value: average((q) => q.value) ?? p.value,
      previous: p.previous === null ? null : average((q) => q.previous),
      parts: null
    };
  });

export type CalendarCell = {
  /** `YYYY-MM-DD`. */
  day: string;
  value: number | null;
  parts: ReportPoint["parts"];
  isWeekend: boolean;
};

export type CalendarWeek = {
  /** The Monday the week starts on, `YYYY-MM-DD`. */
  start: string;
  cells: CalendarCell[];
  /** Set on the first week of each month, for the header. */
  monthLabel: string | null;
};

const dayOf = (iso: string): Date => new Date(`${iso}T00:00:00Z`);
const isoOf = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * A day series laid out as calendar weeks, Monday to Sunday, from the
 * period's first day to its last. Days without a point are empty, not zero.
 */
export const calendarWeeks = (
  series: ReportPoint[],
  from: string,
  to: string
): CalendarWeek[] => {
  const byDay = new Map(series.map((p) => [p.bucket, p]));
  const cursor = dayOf(from);
  // Back to the Monday of the first week.
  const offset = (cursor.getUTCDay() + 6) % 7;
  cursor.setUTCDate(cursor.getUTCDate() - offset);
  const end = dayOf(to);
  const weeks: CalendarWeek[] = [];
  let lastMonth = -1;
  while (cursor <= end && weeks.length < 60) {
    const start = isoOf(cursor);
    const cells: CalendarCell[] = [];
    let monthLabel: string | null = null;
    for (let i = 0; i < 7; i++) {
      const day = isoOf(cursor);
      const inRange = day >= from && day <= to;
      const point = byDay.get(day);
      if (inRange && cursor.getUTCMonth() !== lastMonth) {
        lastMonth = cursor.getUTCMonth();
        monthLabel = cursor.toLocaleDateString("en-US", {
          month: "short",
          timeZone: "UTC"
        });
      }
      cells.push({
        day,
        value: inRange ? (point?.value ?? null) : null,
        parts: point?.parts ?? null,
        isWeekend: i >= 5
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push({ start, cells, monthLabel });
  }
  return weeks;
};

/**
 * The bucket an annotation lands on: the last bucket that starts on or
 * before its day, or null when the day is outside the series.
 */
export const annotationBucket = (
  buckets: string[],
  annotation: ReportAnnotation
): string | null => {
  let found: string | null = null;
  for (const bucket of buckets) {
    if (bucket <= annotation.date) {
      found = bucket;
    } else {
      break;
    }
  }
  return found;
};
